import type { Repo } from '../data'
import type { ID, Player } from '../domain/types'

/**
 * Fill-ins are scoped to a single game.
 *
 * Someone added on the night is not a squad member: they must not appear on any
 * other game's roster, must never be pre-ticked, and must not survive as a
 * permanent player row. A fill-in with a null fillInForGameId was added during a
 * setup that was never started, so it is cleaned up on the next visit.
 *
 * One exception: if stat events somehow reference it, the row is kept. Losing an
 * event is worse than an untidy roster.
 */
export async function purgeAbandonedFillIns(repo: Repo, players: Player[]): Promise<Player[]> {
  const abandoned = players.filter((p) => p.isFillIn && (p.fillInForGameId ?? null) === null)
  if (abandoned.length === 0) return players

  const events = await repo.listAllEvents()
  const referenced = new Set<ID>(events.map((e) => e.playerId))

  const removed = new Set<ID>()
  for (const p of abandoned) {
    if (referenced.has(p.id)) continue
    await repo.deletePlayer(p.id)
    removed.add(p.id)
  }
  return players.filter((p) => !removed.has(p.id))
}

/** Players offered on the setup screen for a given game. */
export function selectableForGame(players: Player[], gameId: ID | null): Player[] {
  return players.filter((p) => {
    if (!p.active) return false
    if (!p.isFillIn) return true
    const scope = p.fillInForGameId ?? null
    // Pending (this setup session) or belonging to the game being set up.
    return scope === null || scope === gameId
  })
}

/** Only real squad members are pre-ticked; never a fill-in. */
export function defaultPresent(players: Player[]): Player[] {
  return players.filter((p) => p.active && !!p.playhqProfileId && !p.isFillIn)
}
